/**
 * RogyogSeoMeta.js
 *
 * SEO + structured data component for the Rogyog (Yoga for Health) section.
 * Mirrors the exact pattern of BlogSeoMeta.js:
 *   - Keyed <Helmet> so React always re-renders on canonical change
 *   - trimDesc() respects word boundaries (155 char cap)
 *   - BreadcrumbList JSON-LD
 *   - ItemList JSON-LD listing active asana videos
 *   - Open Graph + Twitter Card
 */

import React from "react";
import { Helmet } from "react-helmet-async";

// ─── Constants ─────────────────────────────────────────────────────────────────
const SITE_NAME      = "Yoga Shastra";
const SITE_URL       = "https://www.yoga-shastra.com";
const OG_IMAGE       = `${SITE_URL}/og-image.jpg`;
const TWITTER_HANDLE = "@yogashastra";
const CANONICAL      = `${SITE_URL}/#rogYog`;

// ─── Helpers ───────────────────────────────────────────────────────────────────
// Mirrors BlogSeoMeta — word-boundary aware trim
const trimDesc = (str = "", max = 155) => {
  if (!str || str.length <= max) return str;
  return str.slice(0, max).replace(/\s+\S*$/, "").trim() + "…";
};

const ytThumb = (id) =>
  id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : OG_IMAGE;

// ═══════════════════════════════════════════════════════════════════════════════
// RogyogSeoMeta
//
// Props:
//   activeCategory  {string}        — currently selected category name
//   activeSubcat    {string}        — currently selected sub-category name
//   activeVideo     {object|null}   — the video object currently playing
//   videos          {Array}         — all videos in the active sub-category
// ═══════════════════════════════════════════════════════════════════════════════

export const RogyogSeoMeta = ({
  activeCategory = "",
  activeSubcat   = "",
  activeVideo    = null,
  videos         = [],
}) => {
  // ── Dynamic title / description ──────────────────────────────────────────────
  const pageLabel =
    activeVideo?.subCatName
      ? `${activeVideo.subCatName} — ${activeCategory}`
      : activeSubcat
        ? `${activeSubcat} · ${activeCategory}`
        : activeCategory || "Yoga for Health";

  const title       = `${pageLabel} | ${SITE_NAME}`;
  const description = trimDesc(
    activeVideo?.description ||
    `Therapeutic yoga asanas for ${activeSubcat || activeCategory}. ` +
    `Explore guided video practices for healing, balance, and holistic wellbeing — by ${SITE_NAME}.`
  );
  const keywords = [
    "yoga for health",
    "therapeutic yoga",
    "yoga asana",
    activeCategory,
    activeSubcat,
    activeVideo?.subCatName,
    activeVideo?.asnTags,
  ]
    .filter(Boolean)
    .join(", ");

  const ogImage = activeVideo?.asnVideoPath
    ? ytThumb(activeVideo.asnVideoPath)
    : OG_IMAGE;

  // ── BreadcrumbList ───────────────────────────────────────────────────────────
  const breadcrumbItems = [
    { "@type": "ListItem", position: 1, name: "Home",             item: `${SITE_URL}/` },
    { "@type": "ListItem", position: 2, name: "Yoga for Health",  item: CANONICAL },
  ];
  if (activeCategory) {
    breadcrumbItems.push({
      "@type": "ListItem", position: 3, name: activeCategory, item: CANONICAL,
    });
  }
  if (activeSubcat && activeSubcat !== activeCategory) {
    breadcrumbItems.push({
      "@type": "ListItem", position: 4, name: activeSubcat, item: CANONICAL,
    });
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type":    "BreadcrumbList",
    itemListElement: breadcrumbItems,
  };

  // ── ItemList (active video list in subcategory) ───────────────────────────────
  const itemListSchema = videos.length > 0
    ? {
        "@context":       "https://schema.org",
        "@type":          "ItemList",
        name:              `${activeSubcat || activeCategory} — Yoga Asanas`,
        description:       `Therapeutic yoga video practices for ${activeSubcat || activeCategory}.`,
        numberOfItems:     videos.length,
        itemListElement:   videos.slice(0, 20).map((v, idx) => ({
          "@type":    "ListItem",
          position:   idx + 1,
          name:       v.subCatName,
          url:        `https://www.youtube.com/watch?v=${v.asnVideoPath}`,
          image:      ytThumb(v.asnVideoPath),
        })),
      }
    : null;

  // ── VideoObject for active video ─────────────────────────────────────────────
  const videoSchema = activeVideo?.asnVideoPath
    ? {
        "@context":    "https://schema.org",
        "@type":       "VideoObject",
        name:           activeVideo.subCatName,
        description:    trimDesc(activeVideo.description || description),
        thumbnailUrl:   ytThumb(activeVideo.asnVideoPath),
        uploadDate:     "2024-01-01", // static fallback; replace with real date if available
        duration:       activeVideo.asnVideoDuration
                          ? `PT${activeVideo.asnVideoDuration}`
                          : undefined,
        embedUrl:       `https://www.youtube.com/embed/${activeVideo.asnVideoPath}`,
        contentUrl:     `https://www.youtube.com/watch?v=${activeVideo.asnVideoPath}`,
        publisher: {
          "@type": "Organization",
          name:    SITE_NAME,
          url:     SITE_URL,
          logo:    { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
        },
        keywords: activeVideo.asnTags ?? "",
        inLanguage: "en-IN",
      }
    : null;

  // Keyed on title — mirrors BlogSeoMeta so Helmet flushes on selection change
  return (
    <Helmet key={title}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords"    content={keywords} />
      <link rel="canonical"    href={CANONICAL} />

      {/* Open Graph */}
      <meta property="og:type"         content="website" />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={CANONICAL} />
      <meta property="og:image"        content={ogImage} />
      <meta property="og:image:width"  content="1280" />
      <meta property="og:image:height" content="720" />
      <meta property="og:locale"       content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={ogImage} />

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      {itemListSchema && (
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      )}
      {videoSchema && (
        <script type="application/ld+json">{JSON.stringify(videoSchema)}</script>
      )}
    </Helmet>
  );
};
